import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ExportBillingData } from '../../../../../../api-client';

interface UseAutoSaveNotesProps {
  bills: ExportBillingData[];
  onAutoSave?: (billId: string, noteValue: string, updatedBill: ExportBillingData) => Promise<void>;
}

interface UseAutoSaveNotesReturn {
  notes: Record<string, string>;
  updateNote: (billId: string, noteValue: string) => void;
  isSaving: boolean;
  lastSavedAt: Record<string, Date>;
  setAutoSaveHandler: (handler: (billId: string, noteValue: string, updatedBill: ExportBillingData) => Promise<void>) => void;
}

/**
 * Custom hook for auto-saving notes with debouncing
 * Automatically saves notes 3 seconds after the user stops typing
 */
export const useAutoSaveNotes = ({
  bills,
  onAutoSave,
}: UseAutoSaveNotesProps): UseAutoSaveNotesReturn => {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Record<string, Date>>({});
  
  // Store timeouts for each bill's notes
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  
  // Store the original notes to detect changes
  const originalNotesRef = useRef<Record<string, string>>({});
  
  // Store the auto-save handler
  const autoSaveHandlerRef = useRef<((billId: string, noteValue: string, updatedBill: ExportBillingData) => Promise<void>) | null>(null);

  // Store bills and onAutoSave in refs to avoid recreating autoSave
  const billsRef = useRef(bills);
  const onAutoSaveRef = useRef(onAutoSave);
  
  // Store cleanup function that saves pending changes
  const savePendingChangesRef = useRef<() => void>();

  // Update refs when values change
  useEffect(() => {
    billsRef.current = bills;
  }, [bills]);

  useEffect(() => {
    onAutoSaveRef.current = onAutoSave;
  }, [onAutoSave]);

  // Update the cleanup function whenever notes or timeouts change
  useEffect(() => {
    savePendingChangesRef.current = () => {
      const currentTimeouts = timeoutRefs.current;
      const currentNotes = notes;
      const currentOriginalNotes = originalNotesRef.current;
      
      // Save any pending changes immediately
      Object.entries(currentTimeouts).forEach(([billId, timeout]) => {
        clearTimeout(timeout);
        const noteValue = currentNotes[billId];
        const originalNote = currentOriginalNotes[billId] || "";
        
        // Only save if there's actually a change and a note value exists
        if (noteValue && noteValue !== originalNote) {
          const billToUpdate = billsRef.current.find(bill => bill.billId === billId);
          if (billToUpdate) {
            const updatedBill: ExportBillingData = {
              ...billToUpdate,
              notes: noteValue,
            };
            
            // Try to save using available handlers
            if (autoSaveHandlerRef.current) {
              autoSaveHandlerRef.current(billId, noteValue, updatedBill).catch(error => {
                console.error("Failed to save pending changes on unmount:", error);
              });
            } else if (onAutoSaveRef.current) {
              onAutoSaveRef.current(billId, noteValue, updatedBill).catch(error => {
                console.error("Failed to save pending changes on unmount:", error);
              });
            }
          }
        }
      });
      
      // Clear timeouts object
      timeoutRefs.current = {};
    };
  }, [notes]); // Update cleanup function when notes change

  // Set auto-save handler
  const setAutoSaveHandler = useCallback((handler: (billId: string, noteValue: string, updatedBill: ExportBillingData) => Promise<void>) => {
    autoSaveHandlerRef.current = handler;
  }, []);

  // Track bill structure changes
  const billStructure = useMemo(() => ({
    count: bills.length,
    ids: bills.map(b => b.billId).sort().join(',')
  }), [bills]);

  // Initialize notes from bills only when structure changes
  useEffect(() => {
    // Create a snapshot of current state to avoid stale closures
    const currentBills = bills;
    const currentNotes = notes;
    
    const currentBillIds = new Set(Object.keys(currentNotes));
    const newBillIds = new Set(currentBills.map(bill => bill.billId));
    
    // Check if we have new bills or bills were removed
    const hasNewBills = currentBills.some(bill => !currentBillIds.has(bill.billId));
    const hasRemovedBills = Object.keys(currentNotes).some(billId => !newBillIds.has(billId));
    
    if (hasNewBills || hasRemovedBills || Object.keys(currentNotes).length === 0) {
      const initialNotes = currentBills.reduce((acc, bill) => {
        // Preserve existing note if it exists, otherwise use bill's note
        acc[bill.billId] = currentNotes[bill.billId] ?? bill.notes ?? "";
        return acc;
      }, {} as Record<string, string>);
      
      setNotes(initialNotes);
      
      // Only update original notes for completely new bills
      currentBills.forEach(bill => {
        if (!originalNotesRef.current.hasOwnProperty(bill.billId)) {
          originalNotesRef.current[bill.billId] = bill.notes || "";
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billStructure.count, billStructure.ids]);  // Only react to structural changes

  // Auto-save function
  const autoSave = useCallback(async (billId: string, noteValue: string) => {
    try {
      setIsSaving(true);
      
      // Find the bill to update from the current bills
      const billToUpdate = billsRef.current.find(bill => bill.billId === billId);
      if (!billToUpdate) {
        console.warn(`Bill with ID ${billId} not found`);
        return;
      }

      // Create updated bill for backend save
      const updatedBill: ExportBillingData = {
        ...billToUpdate,
        notes: noteValue,
      };

      // Call the auto-save handler if provided (this handles backend save)
      if (autoSaveHandlerRef.current) {
        await autoSaveHandlerRef.current(billId, noteValue, updatedBill);
      } else if (onAutoSaveRef.current) {
        await onAutoSaveRef.current(billId, noteValue, updatedBill);
      }

      // Update last saved timestamp
      setLastSavedAt(prev => ({
        ...prev,
        [billId]: new Date(),
      }));

      // Update original notes reference
      originalNotesRef.current[billId] = noteValue;
      
    } catch (error) {
      console.error("Auto-save failed:", error);

    } finally {
      setIsSaving(false);
    }
  }, []); // Empty dependency array to prevent recreation

  // Update note with debouncing
  const updateNote = useCallback((billId: string, noteValue: string) => {
    // Update notes state immediately for responsive UI
    setNotes(prev => ({
      ...prev,
      [billId]: noteValue,
    }));

    // Clear existing timeout for this bill
    if (timeoutRefs.current[billId]) {
      clearTimeout(timeoutRefs.current[billId]);
    }

    // Only auto-save if the note has actually changed
    const originalNote = originalNotesRef.current[billId] || "";
    if (noteValue !== originalNote) {
      // Set new timeout for auto-save
      timeoutRefs.current[billId] = setTimeout(() => {
        autoSave(billId, noteValue);
        delete timeoutRefs.current[billId];
      }, 3000); // 3 seconds delay
    }
  }, [autoSave]);

  // Cleanup timeouts on unmount and save pending changes
  useEffect(() => {
    return () => {
      // Call the current cleanup function
      if (savePendingChangesRef.current) {
        savePendingChangesRef.current();
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return {
    notes,
    updateNote,
    isSaving,
    lastSavedAt,
    setAutoSaveHandler,
  };
};
